'use client';

import { flex, text, vstack } from '@onbeam/business-styled-system/patterns';
import { BountyListProps } from './BountyList.types';
import { BountyItem } from './BountyItem';
import { css, cx } from '@onbeam/business-styled-system/css';
import { bounties } from './BountyList.data';
import { useBreakpoint } from '@onbeam/utils';
import { token } from '@onbeam/business-styled-system/tokens';
import { breakout } from '@/styles';
import {
  Carousel,
  CarouselContent,
  CarouselControls,
  CarouselItem,
} from '@/components/shared/Carousel';
import { Button } from '@onbeam/business-ui';
import NextLink from 'next/link';

export const BountyList = ({ className, ...props }: BountyListProps) => {
  const isMobile = useBreakpoint({ max: token('breakpoints.md') });

  return (
    <section
      className={cx(
        breakout(),
        vstack({
          flexGrow: 1,
          mdDown: {
            px: '6',
          },
        }),
        className,
      )}
      {...props}
    >
      {!bounties.length ? (
        <div
          className={vstack({
            gap: '2.5',
            align: 'center',
            textAlign: 'center',
            maxW: '[22.9375rem]',
            m: 'auto',
          })}
        >
          <h3 className={text({ style: 'body-lg' })}>
            No bounties available right now
          </h3>
          <p className={text({ style: 'body-md', color: 'mono.tertiary' })}>
            Got an idea for what Beam should build next? Pitch your own project
            through our grant program.
          </p>
          <Button as={NextLink} href="/apply" className={css({ mt: '1.5' })}>
            Apply for grant
          </Button>
        </div>
      ) : isMobile ? (
        <div className={vstack({ gap: '6' })}>
          {bounties.map((bounty) => (
            <BountyItem key={bounty.id} {...bounty} />
          ))}
        </div>
      ) : (
        <Carousel className={vstack({ gap: '5.5', mb: '-12' })}>
          <div
            className={flex({
              position: 'relative',
              align: 'center',
              justify: 'center',
              px: '8',
              w: 'full',
            })}
          >
            <h2
              className={text({
                style: 'body-lg',
              })}
            >
              Available bounties{' '}
              <span className={text({ color: 'mono.tertiary' })}>
                ({String(bounties.length).padStart(2, '0')})
              </span>
            </h2>
            <CarouselControls
              className={css({
                position: 'absolute',
                right: '8',
                top: '[50%]',
                transform: 'translateY(-50%)',
              })}
            />
          </div>
          <CarouselContent
            className={flex({
              pl: '8',
              pb: '12',
            })}
          >
            {bounties.map((bounty) => (
              <CarouselItem
                key={bounty.id}
                className={css({
                  pr: '6',
                  _last: {
                    pr: '8',
                  },
                })}
              >
                <BountyItem {...bounty} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      )}
    </section>
  );
};
